import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Workflow, AlertTriangle, ChevronRight, Loader2, SearchX } from "lucide-react";
import { useListControls, ListToolbar, ListPagination } from "@/components/ListControls";

const critBadge = {
  ALTA: "bg-destructive text-white",
  MEDIA: "bg-[#F59E0B] text-white",
  BASSA: "bg-[#00A859] text-white",
};

const empty = { name: "", category: "", description: "", pain_points: "", linked_tool_ids: [] };

export default function ProcessesPage() {
  const [items, setItems] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const navigate = useNavigate();
  const { canEdit } = useAuth();
  const { activeClientId, activeClient } = useClient();

  const controls = useListControls(items, {
    searchFields: ["name", "category", "description"],
    filters: {
      category: { label: "Categoria", get: (p) => p.category },
      criticality: { label: "Criticità", get: (p) => p.ai_analysis?.criticality_level },
    },
    sortOptions: [
      { value: "name", label: "Nome A–Z", cmp: (a, b) => (a.name || "").localeCompare(b.name || "") },
    ],
    pageSize: 9,
  });

  const load = async () => {
    const params = activeClientId ? { client_id: activeClientId } : {};
    const [p, t] = await Promise.all([api.get("/processes", { params }), api.get("/tools", { params })]);
    setItems(p.data); setTools(t.data); setLoading(false);
  };
  useEffect(() => { load(); }, [activeClientId]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p, e) => {
    e.stopPropagation();
    setForm({ name: p.name, category: p.category || "", description: p.description || "", pain_points: p.pain_points || "", client_id: p.client_id ?? null, linked_tool_ids: p.linked_tool_ids || [] });
    setEditId(p.id); setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nome obbligatorio"); return; }
    try {
      const payload = { ...form, client_id: editId ? form.client_id ?? activeClientId : activeClientId };
      if (editId) await api.put(`/processes/${editId}`, payload);
      else await api.post("/processes", payload);
      toast.success("Processo salvato"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Eliminare questo processo?")) return;
    await api.delete(`/processes/${id}`); toast.success("Processo eliminato"); load();
  };

  const toggleTool = (id) => setForm((f) => ({ ...f, linked_tool_ids: f.linked_tool_ids.includes(id) ? f.linked_tool_ids.filter((x) => x !== id) : [...f.linked_tool_ids, id] }));

  return (
    <Layout>
      <PageHeader title="Processi & Ottimizzazione" subtitle={activeClient ? `${activeClient.name} · ${items.length} processi` : `Portfolio · ${items.length} processi aziendali`}>
        {canEdit && <Button data-testid="add-process-btn" onClick={openNew} className="rounded-none"><Plus className="w-4 h-4 mr-2" /> Nuovo processo</Button>}
      </PageHeader>

      <div className="p-8">
        {loading ? <div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> : items.length === 0 ? (
          <div className="border border-dashed border-border bg-white p-16 text-center">
            <Workflow className="w-10 h-10 text-muted-foreground mx-auto" />
            <p className="text-muted-foreground mt-4">Nessun processo. Crea un processo per analizzarne le criticità.</p>
            {canEdit && <Button onClick={openNew} className="mt-4 rounded-none"><Plus className="w-4 h-4 mr-2" /> Crea il primo processo</Button>}
          </div>
        ) : (
          <>
            <ListToolbar controls={controls} searchPlaceholder="Cerca processo, categoria…" />
            {controls.filtered.length === 0 ? (
              <div className="border border-dashed border-border bg-white p-16 text-center" data-testid="processes-no-results">
                <SearchX className="w-10 h-10 text-muted-foreground mx-auto" />
                <p className="text-muted-foreground mt-4">Nessun processo corrisponde ai filtri.</p>
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="processes-list">
            {controls.visible.map((p, i) => {
              const crit = p.ai_analysis?.criticality_level;
              return (
                <div key={p.id} data-testid={`process-card-${i}`} onClick={() => navigate(`/processes/${p.id}`)} className="bg-white border border-border p-5 cursor-pointer hover:bg-secondary transition-colors duration-150">
                  <div className="flex items-start justify-between">
                    <Workflow className="w-5 h-5 text-primary" />
                    {crit ? <span className={`px-2 py-0.5 text-[10px] font-bold tracking-wide ${critBadge[crit]}`}>{crit}</span> : <span className="data-label">Non analizzato</span>}
                  </div>
                  <h3 className="font-heading font-semibold tracking-tight mt-3">{p.name}</h3>
                  <div className="data-label mt-1">{p.category || "—"}</div>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{p.description}</p>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <div className="flex gap-3">
                      {canEdit && <button onClick={(e) => openEdit(p, e)} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>}
                      {canEdit && <button onClick={(e) => remove(p.id, e)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              );
            })}
          </div>
            )}
            <ListPagination controls={controls} />
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">{editId ? "Modifica processo" : "Nuovo processo"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Nome *</Label><Input data-testid="process-name-input" className="mt-1 rounded-none" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label className="data-label">Categoria</Label><Input className="mt-1 rounded-none" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="es. Sales, Operations" /></div>
            </div>
            <div><Label className="data-label">Descrizione (as-is)</Label><Textarea className="mt-1 rounded-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label className="data-label">Criticità / pain points</Label><Textarea className="mt-1 rounded-none" value={form.pain_points} onChange={(e) => setForm({ ...form, pain_points: e.target.value })} /></div>
            <div>
              <Label className="data-label">Tool IT collegati</Label>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto border border-border p-3">
                {tools.length === 0 ? <p className="text-sm text-muted-foreground">Nessun tool disponibile.</p> : tools.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.linked_tool_ids.includes(t.id)} onCheckedChange={() => toggleTool(t.id)} />
                    {t.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-process-btn" className="rounded-none" onClick={save}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
